import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import { BoldOutlined, ItalicOutlined, UnderlineOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { getIdMap } from 'ontraccr-common/lib/Common';

import Debouncer from '../../../helpers/Debouncer';
import { isFieldLinked } from './PDFDesignerHelpers';
import {
  tiptapToRichText,
  richTextToTiptap,
  plainTextToRichText,
  isLegacyTags,
  convertLegacyTextToRichText,
} from './richTextHelpers';
import VariableMentionList from './VariableMentionList';

// Extend Mention so variable attrs persist in JSON (default schema only has id/label)
const MentionWithVariableAttrs = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fieldId: { default: null },
      sectionId: { default: null },
      nativeKey: { default: null },
      colKey: { default: null },
      supportsFilter: { default: false },
    };
  },
});

const PDF_TEXT_FIELD_TYPES = new Set([
  'text',
  'calculation',
  'dropdown',
  'dateTime',
  'dateRange',
  'attribute',
  'staticText',
]);

const editorStyle = {
  minHeight: '100px',
  padding: '8px',
  border: '1px solid #d9d9d9',
  borderRadius: '4px',
  fontSize: '14px',
  fontFamily: 'inherit',
  textAlign: 'left',
  color: 'inherit',
};

const disabledEditorStyle = {
  ...editorStyle,
  opacity: 0.5,
  cursor: 'not-allowed',
  backgroundColor: '#f5f5f5',
};

const toolbarStyle = {
  display: 'flex',
  gap: '4px',
  marginBottom: '10px',
  padding: '4px',
};

/**
 * Rich Text Editor Component for PDF Designer
 * Provides Bold, Italic, and Underline formatting options
 * Supports variable insertion with # trigger
 * When the field is linked (section + field selected), style toggles apply to the whole text.
 */
export default function PDFRichTextEditor({
  value,
  onChange,
  disabled = false,
  sections = [],
  collected = {},
  tags = [],
  link = {},
  linkTextStyle = {},
  onApplyTextStyles,
}) {
  const [hasConvertedLegacy, setHasConvertedLegacy] = useState(false);

  // convert legacy (plain string + position-based tags)
  if (typeof value === 'string' && isLegacyTags(tags) && !hasConvertedLegacy) {
    setHasConvertedLegacy(true);
    const { segments: convertedSegments } = convertLegacyTextToRichText(value, tags);
    onChange(convertedSegments);
  }

  const flatFields = useMemo(() => (
    sections.flatMap(({ fields: sectionFields = [], name, id: sId }) => (
      sectionFields?.map((field) => {
        const { configProps: { title } = {} } = field ?? {};
        return { ...field, name: `${name} - ${title}`, sectionId: sId };
      }) ?? []
    ))
  ), [sections]);

  const fieldMap = useMemo(() => getIdMap(flatFields), [flatFields]);

  const isFieldLinkedValue = isFieldLinked(link);

  // Build variable list for mention suggestions (matching PDFVariableTextInput whitelist format)
  const variableList = useMemo(() => {
    const opts = [
      {
        id: 'employeeName',
        label: 'Employee Name',
        nativeKey: 'employeeName',
        key: 'employeeName',
      },
      {
        id: 'employeeId',
        label: 'Employee ID',
        nativeKey: 'employeeId',
        key: 'employeeId',
      },
      {
        id: 'formDate',
        label: 'Submission Date',
        nativeKey: 'formDate',
        key: 'formDate',
      },
    ];

    if (collected.number?.collect) {
      opts.push({
        id: 'formNumber',
        label: 'Form Number',
        nativeKey: 'formNumber',
        key: 'formNumber',
      });
    }

    sections.forEach((section) => {
      const { name: sectionName, fields = [], id: sectionId } = section;
      fields.forEach((field) => {
        const {
          configProps: { title } = {},
          id: fieldId,
          selectedType,
        } = field ?? {};

        if (!PDF_TEXT_FIELD_TYPES.has(selectedType) || !title) return;

        opts.push({
          id: fieldId,
          label: `${sectionName} - ${title.trim()}`,
          sectionId,
          fieldId,
          key: fieldId,
        });
      });
    });

    return opts;
  }, [sections, collected]);

  // Debouncer for onChange callback to reduce render cycles
  const debouncerRef = useRef(new Debouncer());

  // Configure Mention extension with # trigger
  const mentionExtension = useMemo(() => (
    MentionWithVariableAttrs.configure({
      HTMLAttributes: {
        class: 'variable-mention',
      },
      suggestion: {
        char: '#',
        items: ({ query }) => (
          variableList
            .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
        ),
        command: ({ editor, range, props }) => {
          const field = props.fieldId ? fieldMap[props.fieldId] : null;
          const supportsFilter = field?.selectedType === 'table'
            && field?.configProps?.dataType === 'TimeEntry';

          const marks = [];
          if (editor.isActive('bold')) marks.push({ type: 'bold' });
          if (editor.isActive('italic')) marks.push({ type: 'italic' });
          if (editor.isActive('underline')) marks.push({ type: 'underline' });

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: 'mention',
                attrs: {
                  id: props.id,
                  label: props.label,
                  fieldId: props.fieldId || null,
                  sectionId: props.sectionId || null,
                  nativeKey: props.nativeKey || null,
                  colKey: props.colKey || null,
                  supportsFilter: supportsFilter || false,
                },
                ...(marks.length > 0 ? { marks } : {}),
              },
            ])
            .run();
        },
        render: () => {
          let component;
          let dropdown;

          return {
            onStart: (props) => {
              component = new ReactRenderer(VariableMentionList, {
                props,
                editor: props.editor,
              });

              if (!props.clientRect) {
                return;
              }

              // Create dropdown element
              dropdown = document.createElement('div');
              dropdown.className = 'variable-mention-dropdown';
              dropdown.style.position = 'absolute';
              dropdown.style.zIndex = '10000';
              dropdown.style.minWidth = '400px';
              dropdown.appendChild(component.element);
              document.body.appendChild(dropdown);

              const updatePosition = () => {
                if (!props.clientRect) return;
                const rect = props.clientRect();
                dropdown.style.top = `${rect.bottom + window.scrollY}px`;
                dropdown.style.left = `${rect.left + window.scrollX}px`;
              };

              updatePosition();
            },

            onUpdate(props) {
              component.updateProps(props);

              if (!props.clientRect) {
                return;
              }

              const rect = props.clientRect();
              dropdown.style.top = `${rect.bottom + window.scrollY}px`;
              dropdown.style.left = `${rect.left + window.scrollX}px`;
            },

            onKeyDown(props) {
              if (props.event.key === 'Escape') {
                if (dropdown?.parentNode) {
                  dropdown.parentNode.removeChild(dropdown);
                }
                return true;
              }

              return component.ref?.onKeyDown(props);
            },

            onExit() {
              if (dropdown?.parentNode) {
                dropdown.parentNode.removeChild(dropdown);
              }
              component.destroy();
            },
          };
        },
      },
    })
  ), [variableList]);

  // Normalize to segments (convert legacy plain string + position-based tags if needed)
  const getNormalizedSegments = useCallback(() => {
    if (!value) return [plainTextToRichText('')[0]];
    if (typeof value === 'string' && isLegacyTags(tags)) {
      return convertLegacyTextToRichText(value, tags).segments;
    }
    return plainTextToRichText(value);
  }, [value, tags]);

  // When field is linked, apply linkTextStyle to every segment for initial content
  const getSegmentsForEditor = useCallback(() => {
    const segments = getNormalizedSegments();
    if (!isFieldLinkedValue || !linkTextStyle || Object.keys(linkTextStyle).length === 0) {
      return segments;
    }
    const { bold, italic, underline } = linkTextStyle;
    return segments.map((seg) => ({
      ...seg,
      ...(bold !== undefined && { bold }),
      ...(italic !== undefined && { italic }),
      ...(underline !== undefined && { underline }),
    }));
  }, [getNormalizedSegments, isFieldLinkedValue, linkTextStyle]);

  // Convert value to Tiptap format
  const getInitialContent = useCallback(() => {
    const segments = getSegmentsForEditor();
    return richTextToTiptap(segments);
  }, [getSegmentsForEditor]);

  const isEditorDisabled = disabled || isFieldLinkedValue;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable all default extensions except what we need
        bold: true,
        italic: true,
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: true, // Enable line breaks (Shift+Enter)
        history: true,
        list: false,
        strike: false,
        paragraph: true, // Ensure paragraphs are enabled (Enter creates new paragraph)
      }),
      Underline,
      mentionExtension,
    ],
    content: getInitialContent(),
    editable: !isEditorDisabled,
    editorProps: {
      attributes: {
        spellcheck: 'false',
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      const tiptapJSON = updatedEditor.getJSON();
      const richText = tiptapToRichText(tiptapJSON);
      debouncerRef.current.debounce(() => onChange(richText), 150);
    },
  });

  const toggleBold = useCallback(() => {
    if (!editor) return;
    if (isFieldLinkedValue) {
      onApplyTextStyles({ bold: !linkTextStyle.bold });
    } else {
      editor.chain().focus().toggleBold()
        .run();
    }
  }, [editor, isFieldLinkedValue, onApplyTextStyles]);

  const toggleItalic = useCallback(() => {
    if (!editor) return;
    if (isFieldLinkedValue) {
      onApplyTextStyles({ italic: !linkTextStyle.italic });
    } else {
      editor.chain().focus().toggleItalic()
        .run();
    }
  }, [editor, isFieldLinkedValue, onApplyTextStyles]);

  const toggleUnderline = useCallback(() => {
    if (!editor) return;
    if (isFieldLinkedValue) {
      onApplyTextStyles({ underline: !linkTextStyle.underline });
    } else {
      editor.chain().focus().toggleUnderline()
        .run();
    }
  }, [editor, isFieldLinkedValue, onApplyTextStyles]);

  useEffect(() => { // eslint-disable-line arrow-body-style
    return () => {
      debouncerRef.current?.clear();
    };
  }, []);

  if (!editor) {
    return null;
  }

  const boldActive = isFieldLinkedValue ? linkTextStyle.bold : editor.isActive('bold');
  const italicActive = isFieldLinkedValue ? linkTextStyle.italic : editor.isActive('italic');
  const underlineActive = isFieldLinkedValue ? linkTextStyle.underline : editor.isActive('underline');

  return (
    <div>
      {!disabled && (
        <div style={toolbarStyle}>
          <Button
            type={boldActive ? 'primary' : 'default'}
            icon={<BoldOutlined />}
            onClick={toggleBold}
            size="small"
            title="Bold"
          />
          <Button
            type={italicActive ? 'primary' : 'default'}
            icon={<ItalicOutlined />}
            onClick={toggleItalic}
            size="small"
            title="Italic"
          />
          <Button
            type={underlineActive ? 'primary' : 'default'}
            icon={<UnderlineOutlined />}
            onClick={toggleUnderline}
            size="small"
            title="Underline"
          />
        </div>
      )}
      <div style={isEditorDisabled ? disabledEditorStyle : editorStyle}>
        <EditorContent editor={editor} spellCheck={false} />
      </div>
    </div>
  );
}

PDFRichTextEditor.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape({
      content: PropTypes.string.isRequired,
      bold: PropTypes.bool,
      italic: PropTypes.bool,
      underline: PropTypes.bool,
    })),
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      selectedType: PropTypes.string,
      configProps: PropTypes.shape({
        title: PropTypes.string,
      }),
    })),
  })),
  collected: PropTypes.shape({
    number: PropTypes.shape({
      collect: PropTypes.bool,
    }),
  }),
  tags: PropTypes.arrayOf(PropTypes.shape({
    startIndex: PropTypes.number,
    endIndex: PropTypes.number,
    fieldId: PropTypes.string,
    sectionId: PropTypes.string,
    nativeKey: PropTypes.string,
    colKey: PropTypes.string,
    supportsFilter: PropTypes.bool,
    label: PropTypes.string,
  })),
  link: PropTypes.shape({
    sectionId: PropTypes.string,
    fieldId: PropTypes.string,
    collectedId: PropTypes.string,
  }),
  linkTextStyle: PropTypes.shape({
    bold: PropTypes.bool,
    italic: PropTypes.bool,
    underline: PropTypes.bool,
  }),
  onApplyTextStyles: PropTypes.func.isRequired,
};

PDFRichTextEditor.defaultProps = {
  value: '',
  disabled: false,
  sections: [],
  collected: {},
  tags: [],
  link: {},
  linkTextStyle: {},
};
